import React from "react";
import { Link } from "react-router-dom";
import { useContent } from "../context/ContentContext";
import { imageUrl } from "../lib/format";
import { Button } from "../components/ui/button";
import { ArrowRight } from "lucide-react";

const Rich = ({ text }) =>
  String(text || "").split("**").map((part, i) => (i % 2 ? <strong key={i} className="text-[#2C1A14]">{part}</strong> : part));

const Paras = ({ text, className = "" }) => (
  <>
    {String(text || "").split(/\n\s*\n/).filter((p) => p.trim()).map((p, i) => (
      <p key={i} className={className}><Rich text={p.trim()} /></p>
    ))}
  </>
);

export default function Histoire() {
  const { content } = useContent();
  if (!content) return <div className="py-32 text-center text-[#9B7E73]">Chargement...</div>;
  const h = content.histoire_page || {};

  return (
    <div className="bg-[#FBF1EE]">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={imageUrl(h.hero_image)} alt="Yolande — Yoyo" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#2C1A14] via-[#2C1A14]/60 to-[#2C1A14]/20" />
        </div>
        <div className="relative mx-auto flex min-h-[60vh] max-w-4xl flex-col items-center justify-end px-4 pb-14 pt-32 text-center sm:px-6">
          <span className="font-script text-4xl text-[#F0B49A] sm:text-5xl">{h.hero_script}</span>
          <h1 className="mt-3 font-serif text-4xl font-semibold leading-tight text-white sm:text-5xl lg:text-6xl" data-testid="histoire-title">{h.hero_title}</h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-[#F3E6E0] sm:text-lg">{h.hero_subtitle}</p>
          <div className="mt-6 flex gap-3">
            {["Mode", "Famille", "Authenticité"].map((t) => (
              <span key={t} className="rounded-full border border-white/40 px-4 py-1 text-xs uppercase tracking-[0.2em] text-white/90">{t}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-16 sm:px-6 lg:py-24">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-5">
          <div className="lg:col-span-3">
            <p className="text-xs uppercase tracking-[0.25em] text-[#D76E4D]">{h.intro_eyebrow}</p>
            <h2 className="mt-2 font-serif text-3xl font-medium text-[#2C1A14] sm:text-4xl">{h.intro_title}</h2>
            <div className="mt-5 space-y-4 text-lg leading-relaxed text-[#6E544A]"><Paras text={h.intro_text} /></div>
          </div>
          <div className="lg:col-span-2">
            <div className="aspect-[3/4] overflow-hidden rounded-2xl shadow-xl ring-1 ring-[#E8D5CE]">
              <img src={imageUrl(h.intro_image)} alt="Pièces du dressing" loading="lazy" className="h-full w-full object-cover" />
            </div>
          </div>
        </div>
        <div className="mx-auto mt-12 max-w-3xl space-y-4 text-center text-lg leading-relaxed text-[#6E544A]"><Paras text={h.middle_text} /></div>
      </section>

      {h.quote && (
        <section className="bg-[#2C1A14] py-16 sm:py-20">
          <div className="mx-auto max-w-3xl px-6 text-center">
            <span className="font-script text-5xl text-[#D76E4D]">“</span>
            <p className="-mt-4 font-serif text-2xl font-medium leading-snug text-[#FBF1EE] sm:text-3xl">{h.quote}</p>
          </div>
        </section>
      )}

      <section className="mx-auto max-w-5xl px-4 py-16 sm:px-6 lg:py-24">
        <div className="text-center">
          <p className="text-xs uppercase tracking-[0.25em] text-[#D76E4D]">{h.vision_eyebrow}</p>
          <h2 className="mt-2 font-serif text-3xl font-medium text-[#2C1A14] sm:text-4xl">{h.vision_title}</h2>
          <div className="mx-auto mt-6 max-w-3xl space-y-4 text-lg leading-relaxed text-[#6E544A]"><Paras text={h.vision_text} /></div>
        </div>
      </section>

      <section className="bg-[#F5E8E3]">
        <div className="mx-auto max-w-3xl px-4 py-16 text-center sm:px-6 lg:py-20">
          <p className="text-xs uppercase tracking-[0.25em] text-[#D76E4D]">{h.dressing_eyebrow}</p>
          <div className="mt-4 space-y-4 text-lg leading-relaxed text-[#6E544A]"><Paras text={h.dressing_text} /></div>
        </div>
      </section>

      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={imageUrl(h.end_image)} alt="Le Dressing de Yoyo" loading="lazy" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-[#2C1A14]/75" />
        </div>
        <div className="relative mx-auto max-w-3xl px-6 py-20 text-center sm:py-28">
          <h2 className="font-serif text-3xl font-semibold text-white sm:text-4xl">{h.end_title}</h2>
          <p className="mt-4 text-lg leading-relaxed text-[#F3E6E0]">{h.end_subtitle}</p>
          <Link to="/catalog">
            <Button data-testid="histoire-cta" className="mt-8 rounded-full bg-[#D76E4D] px-8 py-6 text-base text-white hover:bg-[#C05A3A]">
              Découvrir le Dressing <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
